/**
 * usePlatformENTourTransportLogic
 *
 * [shared]-entity logic hook.
 * Reads/writes the shared-entity store instead of a per-trait
 * reducer — see @almadar/ui/hooks/useSharedEntityStore.
 * Dispatch resolves transitions + effects fully client-side (no
 * server round trip).
 *
 * @generated from schema
 * @trait PlatformENTourTransport
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSharedEntityStoreContext, useSharedEntitySnapshot, useEventBus, useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { emitCascadeEvent } from '@/hooks/useOrbitalBridge';
import { useTranslate } from '@almadar/ui/hooks';
import type { EntityFieldWrite, EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import { mergeEntityFrame } from '@almadar/core';
import type { BusEvent } from '@almadar/core';
import type { EventResponse, PlatformENDemoLaunchScene, PlatformENTourTransportEventPayloadMap } from '@app/shared';

export interface PlatformENTourTransportConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  autoRun: boolean;
  compact: boolean;
  doneLabel?: string;
  matchMode: string;
  ofWord?: string;
  pauseLabel?: string;
  pausedLabel?: string;
  playLabel?: string;
  resetLabel?: string;
  runWhenVisible: boolean;
  runningLabel?: string;
  speedLabel?: string;
  stepIntervalMs: number;
  stepLabel?: string;
  stepWord?: string;
  summary: string;
  title: string;
  totalSteps: number;
}

const DEFAULT_PLATFORM_EN_TOUR_TRANSPORT_CONFIG = {
  autoRun: false,
  compact: true,
  matchMode: "",
  runWhenVisible: false,
  stepIntervalMs: 2200,
  summary: "",
  title: "",
  totalSteps: 0,
} as PlatformENTourTransportConfig;

type PlatformENTourTransportSlot = 'main';
export interface PlatformENTourTransportLogicReturn {
  state: string;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: string | null;
  slotWriter: Partial<Record<PlatformENTourTransportSlot, string | null>>;
  loading: boolean;
  pendingEvent: string | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<PlatformENDemoLaunchScene>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof PlatformENTourTransportEventPayloadMap]: (payload?: PlatformENTourTransportEventPayloadMap[K]) => Promise<EventResponse> };
  config: PlatformENTourTransportConfig;
}

export function usePlatformENTourTransportLogic(config?: Partial<PlatformENTourTransportConfig>): PlatformENTourTransportLogicReturn {
  const mergedConfig: PlatformENTourTransportConfig = { ...DEFAULT_PLATFORM_EN_TOUR_TRANSPORT_CONFIG, ...(config ?? {}) };

  // [shared] entity store + coalesced tick driver
  const _sharedStore = useSharedEntityStoreContext();
  const _sharedEntityId = 'PlatformENDemoLaunchScene';
  _sharedStore.seed(_sharedEntityId, { 'active': false, 'running': false, 'steps': 0, 'elapsedMs': 0, 'done': false, 'cursor': 0, 'shown': -1, 'offscreenPaused': false, 'speed': 1 });
  const _sharedSnapshot = useSharedEntitySnapshot(_sharedStore, _sharedEntityId);

  const fields = _sharedSnapshot as Partial<PlatformENDemoLaunchScene>;
  const data: Record<string, EntityRow[]> = { 'PlatformENDemoLaunchScene': [_sharedSnapshot] };

  // Local per-trait machine state — NOT the shared entity's fields,
  // which always live in the shared store (read fresh on every
  // dispatch, never cached here).
  const [machineState, setMachineState] = useState<string>('idle');
  const [lastPayload, setLastPayload] = useState<EventPayload | undefined>(undefined);
  const [lastEvent, setLastEvent] = useState<string | null>(null);
  const [slotWriter, setSlotWriter] = useState<Partial<Record<PlatformENTourTransportSlot, string | null>>>({});
  const [error, setError] = useState<string | null>(null);
  const machineStateRef = useRef(machineState);
  machineStateRef.current = machineState;

  const eventBus = useEventBus();

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['PlatformENDemoLaunchScene'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'idle' && event === 'INIT' && (true)) {
      // idle → idle (on INIT)
        _fields['active'] = true;
        fields = { ...(fields ?? {}), 'active': _fields['active'] } as Record<string, EventPayloadValue | undefined>;
        _fields['title'] = "";
        fields = { ...(fields ?? {}), 'title': _fields['title'] } as Record<string, EventPayloadValue | undefined>;
        _fields['running'] = false;
        fields = { ...(fields ?? {}), 'running': _fields['running'] } as Record<string, EventPayloadValue | undefined>;
        _fields['steps'] = 0;
        fields = { ...(fields ?? {}), 'steps': _fields['steps'] } as Record<string, EventPayloadValue | undefined>;
        _fields['elapsedMs'] = 0;
        fields = { ...(fields ?? {}), 'elapsedMs': _fields['elapsedMs'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('LEARN_UPDATED', _p, { trait: 'PlatformENTourTransport' }); emitCascadeEvent(eventBus, 'PlatformEN', 'PlatformENTourTransport', { event: 'LEARN_UPDATED', payload: _p, source: { orbital: 'PlatformEN', trait: 'PlatformENTourTransport' } }); })({ steps: fields?.steps, running: fields?.running }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MODE_SELECTED' && ((payloadObj?.mode === ""))) {
      // idle → idle (on MODE_SELECTED)
        _fields['active'] = true;
        fields = { ...(fields ?? {}), 'active': _fields['active'] } as Record<string, EventPayloadValue | undefined>;
        _fields['title'] = "";
        fields = { ...(fields ?? {}), 'title': _fields['title'] } as Record<string, EventPayloadValue | undefined>;
        _fields['running'] = false;
        fields = { ...(fields ?? {}), 'running': _fields['running'] } as Record<string, EventPayloadValue | undefined>;
        _fields['steps'] = 0;
        fields = { ...(fields ?? {}), 'steps': _fields['steps'] } as Record<string, EventPayloadValue | undefined>;
        _fields['elapsedMs'] = 0;
        fields = { ...(fields ?? {}), 'elapsedMs': _fields['elapsedMs'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('LEARN_UPDATED', _p, { trait: 'PlatformENTourTransport' }); emitCascadeEvent(eventBus, 'PlatformEN', 'PlatformENTourTransport', { event: 'LEARN_UPDATED', payload: _p, source: { orbital: 'PlatformEN', trait: 'PlatformENTourTransport' } }); })({ steps: fields?.steps, running: fields?.running }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MODE_SELECTED' && ((!true && !(payloadObj?.mode === "")))) {
      // idle → idle (on MODE_SELECTED)
        _fields['active'] = false;
        fields = { ...(fields ?? {}), 'active': _fields['active'] } as Record<string, EventPayloadValue | undefined>;
        _fields['running'] = false;
        fields = { ...(fields ?? {}), 'running': _fields['running'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'STEP' && (((fields?.active === true) && !(fields?.done === true)))) {
      // idle → idle (on STEP)
        _fields['steps'] = (+(fields?.steps ?? 0) + 1);
        fields = { ...(fields ?? {}), 'steps': _fields['steps'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('LEARN_STEP', _p, { trait: 'PlatformENTourTransport' }); emitCascadeEvent(eventBus, 'PlatformEN', 'PlatformENTourTransport', { event: 'LEARN_STEP', payload: _p, source: { orbital: 'PlatformEN', trait: 'PlatformENTourTransport' } }); })({ steps: fields?.steps }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'TOGGLE_RUN' && ((fields?.active === true))) {
      // idle → idle (on TOGGLE_RUN)
        _fields['running'] = !fields?.running;
        fields = { ...(fields ?? {}), 'running': _fields['running'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('LEARN_UPDATED', _p, { trait: 'PlatformENTourTransport' }); emitCascadeEvent(eventBus, 'PlatformEN', 'PlatformENTourTransport', { event: 'LEARN_UPDATED', payload: _p, source: { orbital: 'PlatformEN', trait: 'PlatformENTourTransport' } }); })({ steps: fields?.steps, running: fields?.running }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'RESET' && ((fields?.active === true))) {
      // idle → idle (on RESET)
        _fields['running'] = false;
        fields = { ...(fields ?? {}), 'running': _fields['running'] } as Record<string, EventPayloadValue | undefined>;
        _fields['steps'] = 0;
        fields = { ...(fields ?? {}), 'steps': _fields['steps'] } as Record<string, EventPayloadValue | undefined>;
        _fields['elapsedMs'] = 0;
        fields = { ...(fields ?? {}), 'elapsedMs': _fields['elapsedMs'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('LEARN_RESET', _p, { trait: 'PlatformENTourTransport' }); emitCascadeEvent(eventBus, 'PlatformEN', 'PlatformENTourTransport', { event: 'LEARN_RESET', payload: _p, source: { orbital: 'PlatformEN', trait: 'PlatformENTourTransport' } }); })({  }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'SPEED_SET' && ((fields?.active === true))) {
      // idle → idle (on SPEED_SET)
        _fields['speed'] = payloadObj?.speed;
        fields = { ...(fields ?? {}), 'speed': _fields['speed'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: PlatformENTourTransportSlot[]; clear: PlatformENTourTransportSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as PlatformENTourTransportSlot[], clear: [] as PlatformENTourTransportSlot[] };
          case 'MODE_SELECTED': return { write: ['main'] as PlatformENTourTransportSlot[], clear: ['main'] as PlatformENTourTransportSlot[] };
          case 'RESET': return { write: ['main'] as PlatformENTourTransportSlot[], clear: [] as PlatformENTourTransportSlot[] };
          case 'SPEED_SET': return { write: ['main'] as PlatformENTourTransportSlot[], clear: [] as PlatformENTourTransportSlot[] };
          case 'STEP': return { write: ['main'] as PlatformENTourTransportSlot[], clear: [] as PlatformENTourTransportSlot[] };
          case 'TOGGLE_RUN': return { write: ['main'] as PlatformENTourTransportSlot[], clear: [] as PlatformENTourTransportSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event against the shared entity, committing locally
   * first (the fields mirror IS the `[runtime]` row).
   * No server round trip — this trait declares `local`.
   */
  const dispatch = useCallback(async (event: string, payload?: EventPayload): Promise<EventResponse> => {
    const prevState = machineStateRef.current;
    const liveData: Record<string, EntityRow[]> = { 'PlatformENDemoLaunchScene': [_sharedStore.getSnapshot(_sharedEntityId)] };
    const result = executeTransitionEffects(prevState, event, liveData, payload);
    if (result.newState === undefined) {
      const errorMsg = `No transition defined for ${event} from ${prevState}`;
      setError(errorMsg);
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const writes: EntityFieldWrite[] = Object.entries(result.fields)
      .filter(([, value]) => value !== undefined)
      .map(([field, value]) => ({ field, value: value as FieldValue }));
    if (writes.length > 0) {
      _sharedStore.commit(_sharedEntityId, mergeEntityFrame(_sharedStore.getSnapshot(_sharedEntityId), writes));
    }
    for (const _emit of result.emits) _emit();
    machineStateRef.current = result.newState;
    setMachineState(result.newState);
    const _renderSlots = getRenderUiSlots(prevState, event);
    if (_renderSlots.write.length > 0 || _renderSlots.clear.length > 0) {
      setLastPayload(payload);
      setLastEvent(event);
      setSlotWriter((prev) => ({
        ...prev,
        ...Object.fromEntries(_renderSlots.write.map((s) => [s, event] as const)),
        ...Object.fromEntries(_renderSlots.clear.map((s) => [s, null] as const)),
      }));
    }
    setError(null);
    return { success: true, transitioned: true, states: { 'PlatformENTourTransport': result.newState }, emittedEvents: [] } as EventResponse;
  }, [executeTransitionEffects, getRenderUiSlots, _sharedStore, _sharedEntityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof PlatformENTourTransportEventPayloadMap]: (payload?: PlatformENTourTransportEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'MODE_SELECTED': (payload?: PlatformENTourTransportEventPayloadMap['MODE_SELECTED']) => dispatch('MODE_SELECTED', payload),
    'STEP': () => dispatch('STEP'),
    'TOGGLE_RUN': () => dispatch('TOGGLE_RUN'),
    'RESET': () => dispatch('RESET'),
    'SPEED_SET': (payload?: PlatformENTourTransportEventPayloadMap['SPEED_SET']) => dispatch('SPEED_SET', payload),
    'LEARN_STEP': (payload?: PlatformENTourTransportEventPayloadMap['LEARN_STEP']) => dispatch('LEARN_STEP', payload),
    'LEARN_RESET': () => dispatch('LEARN_RESET'),
    'LEARN_UPDATED': (payload?: PlatformENTourTransportEventPayloadMap['LEARN_UPDATED']) => dispatch('LEARN_UPDATED', payload),
  };

  useUIEvents(enqueueEvent, 'PlatformEN.PlatformENTourTransport', ['INIT', 'MODE_SELECTED', 'STEP', 'TOGGLE_RUN', 'RESET', 'SPEED_SET'] as const, eventBus);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'TOUR_PLAY' (from PlatformENDemoLaunchTour) → trigger 'TOGGLE_RUN'
    const unsub_tour_play_from_platformendemolaunchtour_to_toggle_run = eventBus.on('UI:PlatformEN.PlatformENDemoLaunchTour.TOUR_PLAY', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'PlatformEN.PlatformENTourTransport', 'TOGGLE_RUN')) return;
      enqueueEvent('TOGGLE_RUN', event.payload);
    });
    return () => {
      if (typeof unsub_tour_play_from_platformendemolaunchtour_to_toggle_run === 'function') unsub_tour_play_from_platformendemolaunchtour_to_toggle_run();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  mergedConfig.doneLabel = mergedConfig.doneLabel ?? (__t("std-almadar-demo-launch:tour.done"));
  mergedConfig.ofWord = mergedConfig.ofWord ?? (__t("std-almadar-demo-launch:tour.ofWord"));
  mergedConfig.pauseLabel = mergedConfig.pauseLabel ?? (__t("std-almadar-demo-launch:tour.pause"));
  mergedConfig.pausedLabel = mergedConfig.pausedLabel ?? (__t("std-almadar-demo-launch:tour.paused"));
  mergedConfig.playLabel = mergedConfig.playLabel ?? (__t("std-almadar-demo-launch:tour.play"));
  mergedConfig.resetLabel = mergedConfig.resetLabel ?? (__t("std-almadar-demo-launch:tour.reset"));
  mergedConfig.runningLabel = mergedConfig.runningLabel ?? (__t("std-almadar-demo-launch:tour.running"));
  mergedConfig.speedLabel = mergedConfig.speedLabel ?? (__t("std-almadar-demo-launch:tour.speed"));
  mergedConfig.stepLabel = mergedConfig.stepLabel ?? (__t("std-almadar-demo-launch:tour.step"));
  mergedConfig.stepWord = mergedConfig.stepWord ?? (__t("std-almadar-demo-launch:tour.stepWord"));
  return {
    state: machineState,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading: false,
    pendingEvent: null,
    pendingFrom: null,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
